import React, { useState, useMemo } from 'react';
import './ResultsView.css';

const compare = (a, b) => {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  const aNum = Number(a);
  const bNum = Number(b);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return aNum - bNum;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

const ResultsView = ({ results }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null); // column index (0-based)
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'
  const rowsPerPage = 50;

  // Move useMemo before early return to satisfy React Hooks rules
  const sortedData = useMemo(() => {
    if (!results || !results.data || results.data.length === 0) return [];
    if (sortColumn == null) return results.data;
    const sorted = [...results.data].sort((rowA, rowB) => {
      const a = rowA[sortColumn];
      const b = rowB[sortColumn];
      const cmp = compare(a, b);
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [results, sortColumn, sortDirection]);

  if (!results || !results.data || results.data.length === 0) {
    return (
      <div className="results-empty">
        <p>No results to display</p>
      </div>
    );
  }

  const totalPages = Math.ceil(sortedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (colIndex) => {
    if (sortColumn === colIndex) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(colIndex);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  return (
    <div className="results-view">
      <div className="table-container">
        <table className="results-table">
          <thead>
            <tr>
              <th className="row-number">#</th>
              {results.columns.map((col, idx) => (
                <th
                  key={idx}
                  className="sortable"
                  onClick={() => handleSort(idx)}
                  title="Click to sort"
                >
                  <span className="th-label">{col}</span>
                  <span className="sort-arrows">
                    <span className={`arrow asc ${sortColumn === idx && sortDirection === 'asc' ? 'active' : ''}`} aria-hidden>▲</span>
                    <span className={`arrow desc ${sortColumn === idx && sortDirection === 'desc' ? 'active' : ''}`} aria-hidden>▼</span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((row, rowIdx) => (
              <tr key={startIndex + rowIdx}>
                <td className="row-number">{startIndex + rowIdx + 1}</td>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>
                    {cell !== null && cell !== undefined ? String(cell) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="results-header">
        <div className="results-info">
          <h3>Results</h3>
          <span className="row-count">
            {results.rowCount} row{results.rowCount !== 1 ? 's' : ''}
          </span>
        </div>
        {totalPages > 1 && (
          <div className="pagination-inline">
            <button
              className="btn-page"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              ← Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-page"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsView;

